import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Tabs,
  Tab,
  Button,
  Paper,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Error as ErrorIcon
} from '@mui/icons-material';
import {
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import SiteIdentity from '../components/SiteIdentity';
import RiskAnalysis from '../components/RiskAnalysis';
import OperatingBudget from '../components/OperatingBudget';
import InvestmentBudget from '../components/InvestmentBudget';
import AssessmentPanel from '../components/assessment/AssessmentPanel';
import { sites, riskAnalyses, operatingBudgets, investmentBudgets, batiments } from '../services/api';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 24 }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function SiteForm({ onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);

  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.role || 'user';
  const userProvince = user?.province || null;

  const initialTab = parseInt(searchParams.get('tab'), 10);
  const [tabValue, setTabValue] = useState(initialTab >= 0 && initialTab <= 3 ? initialTab : 0);
  const [saving, setSaving] = useState(false);
  const [siteData, setSiteData] = useState({
    nom_site: '',
    adresse: '',
    ville: '',
    pays: '',
    destination_site: '',
    statut_juridique: 'proprietaire',
    statut_occupation: '',
    servitude: 'inconnu',
    annee_acquisition: '',
    date_location_debut: '',
    date_location_fin: '',
    surface_jardins: '',
    nombre_batiments: '',
    nombre_personnes_residentes: '',
    nombre_personnes_accueillis: '',
    devise_pays: '',
    province: userRole === 'admin_province' ? userProvince : '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [risks, setRisks] = useState([]);
  // Investissements déjà en base retirés de l'écran : supprimés au prochain enregistrement
  const [deletedRiskIds, setDeletedRiskIds] = useState([]);
  const [opBudgets, setOpBudgets] = useState([]);
  const [invBudgets, setInvBudgets] = useState([]);
  const [buildingsList, setBuildingsList] = useState([]);
  const [errorDialog, setErrorDialog] = useState({ open: false, errors: [], message: '' });
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      loadSiteData();
    } else {
      // Nouveau site : marquer comme chargé immédiatement
      setIsDataLoaded(true);
    }
  }, [id]);

  const loadSiteData = async () => {
    try {
      const [siteResponse, risksResponse, opBudgetsResponse, invBudgetsResponse, batimentsResponse] = await Promise.all([
        sites.getOne(id),
        riskAnalyses.getBySite(id),
        operatingBudgets.getBySite(id),
        investmentBudgets.getBySite(id),
        batiments.getBySite(id)
      ]);

      // Formater les champs date pour les inputs HTML (YYYY-MM-DD)
      const site = siteResponse.data;
      ['date_location_debut', 'date_location_fin'].forEach(field => {
        if (site[field]) {
          const d = new Date(site[field]);
          if (!isNaN(d.getTime())) {
            site[field] = d.toISOString().split('T')[0];
          }
        }
      });

      setSiteData(site);
      // Parser le champ item JSON en array pour chaque risque
      const parsedRisks = risksResponse.data.map(risk => ({
        ...risk,
        item: (() => {
          try {
            const parsed = JSON.parse(risk.item);
            return Array.isArray(parsed) ? parsed : [risk.item];
          } catch {
            return risk.item ? [risk.item] : [];
          }
        })()
      }));
      setRisks(parsedRisks);
      setOpBudgets(opBudgetsResponse.data);
      // Trier invBudgets selon l'ordre des risques (par risk_analysis_id)
      const riskIdOrder = parsedRisks.map(r => r.id);
      const sortedInvBudgets = [...invBudgetsResponse.data].sort((a, b) => {
        return riskIdOrder.indexOf(a.risk_analysis_id) - riskIdOrder.indexOf(b.risk_analysis_id);
      });
      setInvBudgets(sortedInvBudgets);
      setBuildingsList(batimentsResponse.data);
      setIsDataLoaded(true);
    } catch (error) {
      console.error('Error loading site data:', error);
      setIsDataLoaded(true); // Marquer comme chargé même en cas d'erreur
    }
  };

  const handleSiteDataChange = (field, value) => {
    setSiteData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (file) => {
    setImageFile(file);
  };

  const handleRiskRemoved = (riskId) => {
    if (riskId) setDeletedRiskIds(prev => [...prev, riskId]);
  };

  // Le diagnostic écrit directement en base : on recharge pour refléter ses changements
  const handleAssessmentChanged = async ({ investmentsCreated } = {}) => {
    await loadSiteData();
    if (investmentsCreated) setTabValue(1); // amène l'utilisatrice sur l'onglet investissements
  };

  const showSaveError = (error) => {
    console.error('Error saving site:', error);

    // Parse error response
    let errorMessage = t('saveError') || 'Erreur lors de la sauvegarde';
    let errorList = [];

    if (error.response?.data) {
      const data = error.response.data;

      // Handle validation errors array
      if (data.errors && Array.isArray(data.errors)) {
        errorList = data.errors.map(err => ({
          field: err.path || err.param || 'unknown',
          message: err.msg || err.message || 'Erreur de validation'
        }));
      } else if (data.error) {
        errorMessage = data.error;
      } else if (data.message) {
        errorMessage = data.message;
      }
    }

    setErrorDialog({
      open: true,
      errors: errorList,
      message: errorMessage
    });
  };

  const persistForm = async () => {
    const formData = new FormData();

    Object.keys(siteData).forEach(key => {
      if (siteData[key] !== null && siteData[key] !== undefined) {
        formData.append(key, siteData[key]);
      }
    });

    if (imageFile) {
      formData.append('image', imageFile);
    } else if (siteData.image_path) {
      formData.append('existing_image_path', siteData.image_path);
    }

    let siteId;
    if (isEditMode) {
      await sites.update(id, formData);
      siteId = id;
    } else {
      const response = await sites.create(formData);
      siteId = response.data.id;
    }

    // 1) Sauvegarder les bâtiments en PREMIER pour que les FKs des budgets/risques
    //    restent valides. Récupérer les IDs (nouveaux comme existants).
    let savedBuildings = buildingsList || [];
    if (buildingsList && buildingsList.length > 0) {
      const bResp = await batiments.bulkUpdate(siteId, buildingsList);
      savedBuildings = bResp.data?.batiments || buildingsList;
    }

    // Construire une table de remap : numero_batiment → id réel (utile pour les
    // bâtiments nouvellement créés qui n'avaient pas encore d'ID dans le state)
    const buildingIdByNumero = {};
    savedBuildings.forEach(b => {
      if (b.numero_batiment != null) buildingIdByNumero[b.numero_batiment] = b.id;
    });
    // Fonction utilitaire : si batiment_id pointe vers un bâtiment qui n'existe plus
    // dans savedBuildings, on le rebascule via numero_batiment quand c'est possible.
    const savedIdSet = new Set(savedBuildings.map(b => b.id));
    const fallbackBatimentId = (currentBatimentId) => {
      if (!currentBatimentId) return null;
      if (savedIdSet.has(parseInt(currentBatimentId, 10))) return currentBatimentId;
      // Chercher dans buildingsList l'entrée correspondant à cet ancien id
      const orig = buildingsList?.find(b => b.id === currentBatimentId);
      if (orig && orig.numero_batiment != null) {
        return buildingIdByNumero[orig.numero_batiment] || null;
      }
      return null;
    };

    // 2) Supprimer les risques retirés de l'écran (budgets et pièces jointes suivent en cascade)
    for (const riskId of deletedRiskIds) {
      await riskAnalyses.delete(riskId);
    }

    // 3) Créer/Mettre à jour les risques et récupérer leurs IDs
    const riskIds = [];
    for (let i = 0; i < risks.length; i++) {
      const risk = risks[i];
      const riskPayload = {
        ...risk,
        batiment_id: fallbackBatimentId(risk.batiment_id),
        item: JSON.stringify(Array.isArray(risk.item) ? risk.item : [risk.item])
      };
      if (risk.id) {
        await riskAnalyses.update(risk.id, riskPayload);
        riskIds.push(risk.id);
      } else {
        const response = await riskAnalyses.create({ ...riskPayload, site_id: siteId });
        riskIds.push(response.data.id);
      }
    }

    // 4) Mettre à jour les budgets d'exploitation (remap batiment_id si nécessaire)
    const opBudgetsRemapped = opBudgets.map(b => ({
      ...b,
      batiment_id: fallbackBatimentId(b.batiment_id)
    }));
    await operatingBudgets.bulkUpdate(siteId, opBudgetsRemapped);

    // 5) Mettre à jour les budgets d'investissement (avec IDs risques + remap bâtiment).
    //    On part des risques et on retrouve leur budget par ID : si l'onglet budget n'a pas
    //    été ouvert, invBudgets n'est pas resynchronisé et les positions ne correspondent plus.
    //    Les budgets sans ID appartiennent aux nouveaux risques, dans le même ordre.
    const unlinkedBudgets = invBudgets.filter(b => !b.risk_analysis_id);
    let nextUnlinked = 0;
    const invBudgetsWithIds = risks
      .map((risk, index) => {
        const budget = risk.id
          ? invBudgets.find(b => b.risk_analysis_id === risk.id)
          : unlinkedBudgets[nextUnlinked++];
        if (!budget) return null;
        return {
          ...budget,
          risk_analysis_id: riskIds[index],
          batiment_id: fallbackBatimentId(budget.batiment_id)
        };
      })
      .filter(Boolean);
    await investmentBudgets.bulkUpdate(siteId, invBudgetsWithIds);
    setDeletedRiskIds([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistForm();
      navigate('/');
    } catch (error) {
      showSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  // Avant d'ouvrir le diagnostic : enregistre le formulaire et recharge depuis la base,
  // pour que la détection des doublons travaille sur des données à jour.
  const handleBeforeAssessment = async () => {
    setSaving(true);
    try {
      await persistForm();
      setImageFile(null);
      await loadSiteData();
      return true;
    } catch (error) {
      showSaveError(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const getTabIcon = (index) => {
    if (index < tabValue) {
      return <CheckCircleIcon sx={{ fontSize: 20, ml: 1, color: 'success.main' }} />;
    }
    return null;
  };

  const getProgress = () => {
    return ((tabValue + 1) / 4) * 100;
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ py: 1 }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isEditMode ? siteData.nom_site || 'Modification du site' : t('addSite')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Étape {tabValue + 1} sur 4
            </Typography>
          </Box>
          <Chip
            label={`${Math.round(getProgress())}%`}
            color="secondary"
            size="small"
            sx={{ mr: 2, fontWeight: 600 }}
          />
          <IconButton color="inherit" onClick={onLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
        <LinearProgress
          variant="determinate"
          value={getProgress()}
          sx={{
            height: 3,
            backgroundColor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'secondary.main',
            }
          }}
        />
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <AssessmentPanel
          siteId={id}
          buildings={buildingsList}
          onBeforeLaunch={handleBeforeAssessment}
          onChanged={handleAssessmentChanged}
        />
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{
            borderBottom: 1,
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  py: 2.5,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  minHeight: 72,
                },
                '& .Mui-selected': {
                  color: 'primary.main',
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('siteIdentity')}
                    {getTabIcon(0)}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('riskAnalysis')}
                    {getTabIcon(1)}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('operatingBudgetTab')}
                    {getTabIcon(2)}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('investmentBudgetTab')}
                    {getTabIcon(3)}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          <Box sx={{ p: 4 }}>
            <TabPanel value={tabValue} index={0}>
              <SiteIdentity
                data={siteData}
                onChange={handleSiteDataChange}
                onImageChange={handleImageChange}
                batiments={buildingsList}
                setBatiments={setBuildingsList}
                userRole={userRole}
                userProvince={userProvince}
                siteId={id}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <RiskAnalysis
                risks={risks}
                setRisks={setRisks}
                onRiskRemoved={handleRiskRemoved}
                userRole={userRole}
                siteId={id}
                buildings={buildingsList}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <OperatingBudget
                budgets={opBudgets}
                setBudgets={setOpBudgets}
                isDataLoaded={isDataLoaded}
                buildings={buildingsList}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <InvestmentBudget
                budgets={invBudgets}
                setBudgets={setInvBudgets}
                risks={risks}
                setRisks={setRisks}
                buildings={buildingsList}
              />
            </TabPanel>
          </Box>

          <Box
            sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Box>
              {tabValue > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setTabValue(tabValue - 1)}
                  sx={{ mr: 2 }}
                >
                  {t('previous')}
                </Button>
              )}
              {tabValue < 3 && (
                <Button
                  variant="contained"
                  onClick={() => setTabValue(tabValue + 1)}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {t('next')}
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={() => navigate('/')}
                disabled={saving}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  minWidth: 140,
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1E8449 0%, #27AE60 100%)',
                  },
                }}
              >
                {saving ? 'Enregistrement...' : t('save')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Error Dialog */}
      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, errors: [], message: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main',
          fontWeight: 600
        }}>
          <ErrorIcon />
          {t('errorTitle') || 'Erreur'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorDialog.message}
          </Alert>

          {errorDialog.errors.length > 0 && (
            <List dense>
              {errorDialog.errors.map((err, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        <strong>{t(`fields.${err.field}`) || err.field}:</strong> {err.message}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialog({ open: false, errors: [], message: '' })}
            variant="contained"
            color="primary"
          >
            {t('close') || 'Fermer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SiteForm;
